/**
 * Greenzone whitelist portal automation (CAR-41).
 *
 * Bridge4IP confirmed that the Greenzone host/IP whitelist is OCS portal-only
 * (no REST API). This module drives Sam's Chrome session via the Kapture MCP
 * to manage entries in the "Manage hosts" and "Manage IPs" menus, and persists
 * synced state to KV for fast reads and hourly reconciliation.
 *
 * FLOW per mutation tool:
 *   1. Open own tab via Kapture new_tab (tabId stored, never reused across calls).
 *   2. Navigate to the OCS portal Greenzone section.
 *   3. Click / fill / screenshot to verify.
 *   4. Write result to KV (GREENZONE_STATE_KV).
 *   5. Close tab — always, on success AND error paths.
 *
 * RECONCILIATION:
 *   reconcileGreenzoneState() is called hourly by the ocs-bridge cron. It
 *   lists current portal state (read-only Kapture flow) and compares against
 *   KV, logging any drift. It does NOT auto-correct drift — only flags it so
 *   an operator can review. When last_synced_at is newer than last_reconciled_at,
 *   KV host/IP lists are preserved so optimistic tool writes are not erased
 *   before portal automation completes (parity with apps/mcp-server).
 *
 * KV schema (GREENZONE_STATE_KV namespace, key "greenzone:state"):
 *   {
 *     hosts: string[];          // whitelisted hostnames
 *     ips:   string[];          // whitelisted CIDR/IP strings
 *     last_synced_at: string;   // ISO 8601 UTC
 *     last_reconciled_at?: string;
 *     reconcile_drift?: boolean;
 *   }
 *
 * KAPTURE DEPENDENCY:
 *   Kapture is a Chrome extension running in Sam's daily browser. It must be
 *   active for portal automation to work. If Kapture is unavailable (extension
 *   sleeping / no connected browser), tools return a clear error with remediation
 *   instructions. Portal authentication is handled automatically via Sam's
 *   existing Chrome session — no credential injection required.
 *
 * SMOKE TEST NOTE:
 *   End-to-end portal flows require Sam's Chrome + Kapture extension awake.
 *   Unit tests cover state-management logic only. Integration smoke test is
 *   documented in tests/greenzone-whitelist.test.ts and must be run manually
 *   when portal access is available.
 */

import type { Env } from "./types.js";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATE_KEY = "greenzone:state";

/** URL path to the Greenzone section — adjust if portal routing changes. */
const GREENZONE_PORTAL_PATH = "/greenzone";
const MANAGE_HOSTS_PATH = "/greenzone/hosts";
const MANAGE_IPS_PATH = "/greenzone/ips";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GreenzoneEntry {
  host?: string;
  ip?: string;
}

export interface GreenzoneState {
  hosts: string[];
  ips: string[];
  last_synced_at: string;
  last_reconciled_at?: string;
  reconcile_drift?: boolean;
}

export interface GreenzoneOperationResult {
  ok: boolean;
  message: string;
  state?: GreenzoneState;
  /** Screenshot data URI taken after the operation for audit. */
  screenshot_taken?: boolean;
}

// ---------------------------------------------------------------------------
// KV state helpers
// ---------------------------------------------------------------------------

/**
 * Read current persisted state from KV.
 * Returns a blank state (empty lists) when no data exists yet.
 */
export async function readState(kv: KVNamespace): Promise<GreenzoneState> {
  const raw = await kv.get(STATE_KEY, "json");
  if (!raw) {
    return {
      hosts: [],
      ips: [],
      last_synced_at: new Date().toISOString(),
    };
  }
  return raw as GreenzoneState;
}

/**
 * Persist state to KV. TTL of 7 days — reconciliation refreshes it hourly.
 */
export async function writeState(kv: KVNamespace, state: GreenzoneState): Promise<void> {
  await kv.put(STATE_KEY, JSON.stringify(state), {
    expirationTtl: 60 * 60 * 24 * 7, // 7 days
  });
}

/**
 * Add a host to the in-memory state (deduplicating). Returns new state.
 */
export function stateAddHost(state: GreenzoneState, host: string): GreenzoneState {
  const normalised = host.toLowerCase().trim();
  if (state.hosts.includes(normalised)) return state;
  return {
    ...state,
    hosts: [...state.hosts, normalised].sort(),
    last_synced_at: new Date().toISOString(),
  };
}

/**
 * Remove a host from the in-memory state. Returns new state.
 */
export function stateRemoveHost(state: GreenzoneState, host: string): GreenzoneState {
  const normalised = host.toLowerCase().trim();
  return {
    ...state,
    hosts: state.hosts.filter((h) => h !== normalised),
    last_synced_at: new Date().toISOString(),
  };
}

/**
 * Add an IP/CIDR to the in-memory state (deduplicating). Returns new state.
 */
export function stateAddIp(state: GreenzoneState, ip: string): GreenzoneState {
  const normalised = ip.trim();
  if (state.ips.includes(normalised)) return state;
  return {
    ...state,
    ips: [...state.ips, normalised].sort(),
    last_synced_at: new Date().toISOString(),
  };
}

/**
 * Remove an IP/CIDR from the in-memory state. Returns new state.
 */
export function stateRemoveIp(state: GreenzoneState, ip: string): GreenzoneState {
  const normalised = ip.trim();
  return {
    ...state,
    ips: state.ips.filter((i) => i !== normalised),
    last_synced_at: new Date().toISOString(),
  };
}

/**
 * Compute drift between portal state (source of truth) and KV state.
 * Returns true if any discrepancy is detected.
 */
export function hasDrift(kvState: GreenzoneState, portalState: GreenzoneState): boolean {
  const hostsMatch =
    kvState.hosts.length === portalState.hosts.length &&
    kvState.hosts.every((h, i) => h === portalState.hosts[i]);
  const ipsMatch =
    kvState.ips.length === portalState.ips.length &&
    kvState.ips.every((ip, i) => ip === portalState.ips[i]);
  return !hostsMatch || !ipsMatch;
}

// ---------------------------------------------------------------------------
// Kapture portal automation
// ---------------------------------------------------------------------------

/**
 * Kapture MCP client shim.
 *
 * Kapture runs in Sam's Chrome extension. We call it via the Claude Code MCP
 * surface (mcp__kapture__* tools). In the server context (CF Worker) these
 * calls are not available — the Greenzone tools are designed to be invoked
 * by Claude Code, which bridges to Kapture. The functions below build the
 * structured call descriptors that the MCP tool handlers return as
 * `kapture_steps` for the caller to execute.
 *
 * When invoked from mcp-stdio (local Claude Desktop), the caller is Claude
 * Code itself — it can invoke Kapture MCP tools directly as part of the
 * tool chain. When invoked from mcp-server (CF Worker), the handler returns
 * a `requires_kapture: true` flag so the caller knows it must relay through
 * Kapture.
 *
 * This design keeps the CF Worker stateless (no direct Kapture calls from
 * edge) while still providing a complete automation description to Claude Code.
 */

export interface KaptureStep {
  tool:
    | "new_tab"
    | "navigate"
    | "click"
    | "fill"
    | "find"
    | "screenshot"
    | "close";
  args?: Record<string, unknown>;
  description: string;
}

/**
 * Escape a string for embedding inside double-quoted `:has-text("…")` selector fragments
 * (backslashes and double quotes would otherwise break parsing).
 */
function escapeForHasTextDoubleQuoted(fragment: string): string {
  return fragment.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

/**
 * Build the Kapture step sequence for adding a host to the Greenzone whitelist.
 */
export function buildAddHostSteps(
  portalBaseUrl: string,
  host: string,
): KaptureStep[] {
  return [
    {
      tool: "new_tab",
      description: "Open a dedicated tab for Greenzone host whitelist operation",
    },
    {
      tool: "navigate",
      args: { url: `${portalBaseUrl}${MANAGE_HOSTS_PATH}` },
      description: `Navigate to Manage hosts page at ${portalBaseUrl}${MANAGE_HOSTS_PATH}`,
    },
    {
      tool: "find",
      args: { query: "Add host button or input field" },
      description: "Find the 'Add host' button or input in Manage hosts menu",
    },
    {
      tool: "click",
      args: { selector: "[data-action='add-host'], button:has-text('Add'), a:has-text('Add host')" },
      description: "Click Add host to open the entry form",
    },
    {
      tool: "fill",
      args: { selector: "input[name='host'], input[placeholder*='host'], input[type='text']", value: host },
      description: `Fill in host value: ${host}`,
    },
    {
      tool: "click",
      args: { selector: "button[type='submit'], button:has-text('Save'), button:has-text('Confirm')" },
      description: "Submit the form to save the host",
    },
    {
      tool: "screenshot",
      description: "Screenshot to verify host was added successfully",
    },
    {
      tool: "close",
      description: "Close the tab (always runs — success or error)",
    },
  ];
}

/**
 * Build the Kapture step sequence for removing a host from the Greenzone whitelist.
 */
export function buildRemoveHostSteps(
  portalBaseUrl: string,
  host: string,
): KaptureStep[] {
  const hostKey = host.toLowerCase().trim();
  const hostText = escapeForHasTextDoubleQuoted(hostKey);
  return [
    {
      tool: "new_tab",
      description: "Open a dedicated tab for Greenzone host removal operation",
    },
    {
      tool: "navigate",
      args: { url: `${portalBaseUrl}${MANAGE_HOSTS_PATH}` },
      description: `Navigate to Manage hosts page at ${portalBaseUrl}${MANAGE_HOSTS_PATH}`,
    },
    {
      tool: "find",
      args: { query: `row containing host ${hostKey}` },
      description: `Find the row for host: ${hostKey}`,
    },
    {
      tool: "click",
      args: {
        selector: `tr:has-text("${hostText}") [data-action='delete'], tr:has-text("${hostText}") button:has-text('Remove'), tr:has-text("${hostText}") button:has-text('Delete')`,
      },
      description: `Click Remove/Delete for host: ${hostKey}`,
    },
    {
      tool: "click",
      args: { selector: "button:has-text('Confirm'), button:has-text('Yes'), [data-action='confirm']" },
      description: "Confirm the deletion in the confirmation dialog",
    },
    {
      tool: "screenshot",
      description: "Screenshot to verify host was removed",
    },
    {
      tool: "close",
      description: "Close the tab (always runs — success or error)",
    },
  ];
}

/**
 * Build the Kapture step sequence for adding an IP/CIDR to the Greenzone whitelist.
 */
export function buildAddIpSteps(
  portalBaseUrl: string,
  ip: string,
): KaptureStep[] {
  return [
    {
      tool: "new_tab",
      description: "Open a dedicated tab for Greenzone IP whitelist operation",
    },
    {
      tool: "navigate",
      args: { url: `${portalBaseUrl}${MANAGE_IPS_PATH}` },
      description: `Navigate to Manage IPs page at ${portalBaseUrl}${MANAGE_IPS_PATH}`,
    },
    {
      tool: "find",
      args: { query: "Add IP button or input field" },
      description: "Find the 'Add IP' button or input in Manage IPs menu",
    },
    {
      tool: "click",
      args: { selector: "[data-action='add-ip'], button:has-text('Add'), a:has-text('Add IP')" },
      description: "Click Add IP to open the entry form",
    },
    {
      tool: "fill",
      args: { selector: "input[name='ip'], input[placeholder*='IP'], input[placeholder*='ip'], input[type='text']", value: ip },
      description: `Fill in IP value: ${ip}`,
    },
    {
      tool: "click",
      args: { selector: "button[type='submit'], button:has-text('Save'), button:has-text('Confirm')" },
      description: "Submit the form to save the IP",
    },
    {
      tool: "screenshot",
      description: "Screenshot to verify IP was added successfully",
    },
    {
      tool: "close",
      description: "Close the tab (always runs — success or error)",
    },
  ];
}

/**
 * Build the Kapture step sequence for removing an IP/CIDR from the Greenzone whitelist.
 */
export function buildRemoveIpSteps(
  portalBaseUrl: string,
  ip: string,
): KaptureStep[] {
  const ipKey = ip.trim();
  const ipText = escapeForHasTextDoubleQuoted(ipKey);
  return [
    {
      tool: "new_tab",
      description: "Open a dedicated tab for Greenzone IP removal operation",
    },
    {
      tool: "navigate",
      args: { url: `${portalBaseUrl}${MANAGE_IPS_PATH}` },
      description: `Navigate to Manage IPs page at ${portalBaseUrl}${MANAGE_IPS_PATH}`,
    },
    {
      tool: "find",
      args: { query: `row containing IP ${ipKey}` },
      description: `Find the row for IP: ${ipKey}`,
    },
    {
      tool: "click",
      args: {
        selector: `tr:has-text("${ipText}") [data-action='delete'], tr:has-text("${ipText}") button:has-text('Remove'), tr:has-text("${ipText}") button:has-text('Delete')`,
      },
      description: `Click Remove/Delete for IP: ${ipKey}`,
    },
    {
      tool: "click",
      args: { selector: "button:has-text('Confirm'), button:has-text('Yes'), [data-action='confirm']" },
      description: "Confirm the deletion in the confirmation dialog",
    },
    {
      tool: "screenshot",
      description: "Screenshot to verify IP was removed",
    },
    {
      tool: "close",
      description: "Close the tab (always runs — success or error)",
    },
  ];
}

/**
 * Build the Kapture step sequence for listing all Greenzone whitelist entries.
 * This is a read-only flow: open tab → navigate → scrape → close.
 */
export function buildListSteps(portalBaseUrl: string): KaptureStep[] {
  return [
    {
      tool: "new_tab",
      description: "Open a dedicated tab for Greenzone whitelist read",
    },
    {
      tool: "navigate",
      args: { url: `${portalBaseUrl}${GREENZONE_PORTAL_PATH}` },
      description: `Navigate to Greenzone overview at ${portalBaseUrl}${GREENZONE_PORTAL_PATH}`,
    },
    {
      tool: "find",
      args: { query: "Manage hosts and Manage IPs sections or links" },
      description: "Find Manage hosts and Manage IPs menu items",
    },
    {
      tool: "screenshot",
      description: "Screenshot of Greenzone overview for audit",
    },
    {
      tool: "navigate",
      args: { url: `${portalBaseUrl}${MANAGE_HOSTS_PATH}` },
      description: "Navigate to Manage hosts to read current host list",
    },
    {
      tool: "find",
      args: { query: "host entries table or list" },
      description: "Read all host entries from the page",
    },
    {
      tool: "navigate",
      args: { url: `${portalBaseUrl}${MANAGE_IPS_PATH}` },
      description: "Navigate to Manage IPs to read current IP list",
    },
    {
      tool: "find",
      args: { query: "IP entries table or list" },
      description: "Read all IP entries from the page",
    },
    {
      tool: "close",
      description: "Close the tab (always runs)",
    },
  ];
}

// ---------------------------------------------------------------------------
// Reconciliation logic
// ---------------------------------------------------------------------------

/**
 * Compare portal-scraped state against KV state and update KV with drift flag.
 *
 * Called by the hourly reconciliation cron in ocs-bridge.
 * Does NOT auto-correct drift — flags it in KV for operator review.
 * When `last_synced_at` is newer than `last_reconciled_at`, KV lists are left
 * unchanged so optimistic tool writes are not erased before portal automation
 * completes.
 *
 * @param kv         The GREENZONE_STATE_KV namespace.
 * @param portalHosts Hosts scraped from the portal.
 * @param portalIps   IPs scraped from the portal.
 * @returns           The updated KV state with reconcile metadata.
 */
export async function reconcileGreenzoneState(
  kv: KVNamespace,
  portalHosts: string[],
  portalIps: string[],
): Promise<GreenzoneState> {
  const kvState = await readState(kv);

  const normalisedPortalHosts = [
    ...new Set(portalHosts.map((h) => h.toLowerCase().trim()).filter((h) => h.length > 0)),
  ].sort();

  const normalisedPortalIps = [
    ...new Set(portalIps.map((ip) => ip.trim()).filter((ip) => ip.length > 0)),
  ].sort();

  const portalState: GreenzoneState = {
    hosts: normalisedPortalHosts,
    ips: normalisedPortalIps,
    last_synced_at: kvState.last_synced_at,
  };

  const sortedKvState: GreenzoneState = {
    ...kvState,
    hosts: [...kvState.hosts].sort(),
    ips: [...kvState.ips].sort(),
  };

  const drift = hasDrift(sortedKvState, portalState);

  const kvMutatedSinceLastReconcile =
    kvState.last_reconciled_at != null && kvState.last_synced_at > kvState.last_reconciled_at;

  const reconciled: GreenzoneState = {
    hosts: kvMutatedSinceLastReconcile ? sortedKvState.hosts : portalState.hosts,
    ips: kvMutatedSinceLastReconcile ? sortedKvState.ips : portalState.ips,
    last_synced_at: kvState.last_synced_at,
    last_reconciled_at: new Date().toISOString(),
    reconcile_drift: drift,
  };

  await writeState(kv, reconciled);
  return reconciled;
}

// ---------------------------------------------------------------------------
// Env accessor — resolves portal URL
// ---------------------------------------------------------------------------

export function resolvePortalBaseUrl(env: Env): string {
  return (
    (env as unknown as { GREENZONE_PORTAL_URL?: string }).GREENZONE_PORTAL_URL ??
    env.CARRIER_OCS_PORTAL_URL ??
    env.CARRIER_OCS_BASE_URL ??
    "https://ocs.esimvault.cloud"
  );
}
